import React from 'react';
import PropTypes from 'prop-types';
import { truncate } from 'lodash-es';
import dayjs from 'util/date';

import PublicationStatusFlags from 'components/Shared/PublicationStatusFlags';
import ResearchOutput from 'components/Explorer/models/research_output';
import CurrentsBreakdownBar from './CurrentsBreakdownBar';
import CurrentsMetricsList from './CurrentsMetricsList';

// FoR 2020 two-digit division codes → emoji
const FOR_EMOJI = {
  30: '\uD83C\uDF3E', // 🌾 Agricultural
  31: '\uD83E\uDDEC', // 🧬 Biological
  32: '\uD83C\uDFE5', // 🏥 Biomedical
  33: '\uD83C\uDFD7', // 🏗️ Built environment
  34: '\uD83E\uDDEA', // 🧪 Chemical
  35: '\uD83D\uDCCA', // 📊 Commerce
  36: '\uD83C\uDFA8', // 🎨 Creative arts
  37: '\uD83C\uDF0D', // 🌍 Earth sciences
  38: '\uD83D\uDCB0', // 💰 Economics
  39: '\uD83D\uDCDA', // 📚 Education
  40: '\u2699\uFE0F', // ⚙️ Engineering
  41: '\uD83C\uDF3F', // 🌿 Environmental
  42: '\uD83D\uDC8A', // 💊 Health
  43: '\uD83C\uDFDB', // 🏛️ History
  44: '\uD83D\uDC65', // 👥 Human society
  45: '\uD83C\uDF0F', // 🌏 Indigenous studies
  46: '\uD83D\uDCBB', // 💻 Computing
  47: '\uD83D\uDDE3', // 🗣️ Language
  48: '\u2696\uFE0F', // ⚖️ Law
  49: '\u2795', // ➕ Mathematical
  50: '\uD83E\uDD14', // 🤔 Philosophy
  51: '\u269B\uFE0F', // ⚛️ Physical sciences
  52: '\uD83E\uDDE0' // 🧠 Psychology
};

function forEmoji(code) {
  const division = code.substring(0, 2);
  return FOR_EMOJI[division] || '';
}

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const date = dayjs(dateStr).utc();
  const now = dayjs();
  const diffDays = now.diff(date, 'day');

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

export const SENTIMENT_KEYS = [
  'strong_negative',
  'negative',
  'neutral_negative',
  'neutral',
  'neutral_positive',
  'positive',
  'strong_positive'
];

function computeSentiment(raw) {
  if (!raw) return null;
  const total = SENTIMENT_KEYS.reduce((sum, k) => sum + (raw[k] || 0), 0);
  if (total === 0) return null;
  const pcts = {};
  SENTIMENT_KEYS.forEach((k) => (pcts[k] = Math.round(((raw[k] || 0) / total) * 100)));
  const positivePct = pcts.neutral_positive + pcts.positive + pcts.strong_positive;
  return { pcts, positivePct };
}

const CurrentsCard = ({ output, rank, onClick, mentionContext, mentionColor, timeframe }) => {
  const handleClick = (event) => {
    event.preventDefault();
    onClick(output);
  };

  const fullDate = output.pubdate ? dayjs(output.pubdate).utc().format('D MMMM YYYY') : null;
  const relative = relativeTime(output.pubdate);
  const sentiment = computeSentiment(output.sentiment);

  return (
    <button type="button" className="CurrentsCard" onClick={handleClick}>
      {rank != null && <span className="CurrentsCard-rank">{rank}</span>}
      <div className="CurrentsCard-badge">
        <img
          className="CurrentsCard-badge-img"
          loading="lazy"
          src={output.badgeURL}
          alt={I18n.t('Explorer.Currents.card.badge_alt', { score: output.score })}
          width="64"
          height="64"
        />
      </div>

      <div className="CurrentsCard-content">
        <h3 className="CurrentsCard-title">
          <PublicationStatusFlags output={output} />
          {truncate(output.title, { length: 160, separator: /\W/, omission: '\u2026' })}
        </h3>

        <div className="CurrentsCard-meta">
          <span className="CurrentsCard-type">{output.type}</span>
          {output.journal && (
            <>
              {' in '}
              <span className="CurrentsCard-journal">{output.journal}</span>
            </>
          )}
          {fullDate && (
            <span className="CurrentsCard-pubdate">
              {' '}
              &middot; Published {fullDate}
              {relative ? ` (${relative})` : ''}
            </span>
          )}
        </div>

        {sentiment && (
          <div className="CurrentsCard-sentiment">
            <span className="CurrentsCard-sentiment-bar">
              {SENTIMENT_KEYS.map((k) =>
                sentiment.pcts[k] > 0 ? (
                  <span
                    key={k}
                    className={`sentiment-segment sentiment-${k}`}
                    style={{ width: `${sentiment.pcts[k]}%` }}
                  />
                ) : null
              )}
            </span>
            <span className="CurrentsCard-sentiment-pct">{sentiment.positivePct}%+</span>
            <span className="CurrentsCard-sentiment-label">{I18n.t('Explorer.Currents.sentiment.card_label')}</span>
          </div>
        )}

        {mentionContext && output.countForSort > 0 && (
          <div className="CurrentsCard-mentions" style={mentionColor ? { color: mentionColor } : undefined}>
            <svg className="CurrentsCard-mentions-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="CurrentsCard-mentions-count">{output.countForSort.toLocaleString()}</span>{' '}
            <span className="CurrentsCard-mentions-label">{mentionContext}</span>
            {output.timeframeMentions != null &&
              output.timeframeMentions !== output.countForSort &&
              output.timeframeMentions > 0 && (
                <span className="CurrentsCard-mentions-timeframe">
                  ({output.timeframeMentions.toLocaleString()} {I18n.t(`Explorer.Currents.timeframes.${timeframe}`)})
                </span>
              )}
            {output.totalMentions != null &&
              output.totalMentions !== output.countForSort &&
              output.totalMentions > 0 && (
                <span className="CurrentsCard-mentions-total">
                  ({output.totalMentions.toLocaleString()} {I18n.t('Explorer.Currents.total_mentions')})
                </span>
              )}
          </div>
        )}

        <CurrentsBreakdownBar mentionCounts={output.mentionCounts} />

        {output.fieldsOfResearch && output.fieldsOfResearch.length > 0 && (
          <div className="CurrentsCard-fields">
            {output.fieldsOfResearch.slice(0, 3).map((field) => (
              <span key={field.code} className="CurrentsCard-field-chip">
                {forEmoji(field.code)} {field.name}
              </span>
            ))}
            {output.fieldsOfResearch.length > 3 && (
              <span className="CurrentsCard-field-chip CurrentsCard-field-chip-more">
                +{output.fieldsOfResearch.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      <CurrentsMetricsList mentionCounts={output.mentionCounts} />
    </button>
  );
};

CurrentsCard.propTypes = {
  output: PropTypes.instanceOf(ResearchOutput).isRequired,
  rank: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  mentionContext: PropTypes.string,
  mentionColor: PropTypes.string,
  timeframe: PropTypes.string
};

export default CurrentsCard;
